<template>
	<hr
		ref="divider"
		class="flow-divider"
		:size="size"
		:state="state"
		:type="type"
		:direction="direction"
		:data-resize-siblings="resizeSiblings"
		:data-active="resize"
		@mousedown="handleMouseDown"
	/>
</template>
<script lang="ts">
import { defineComponent, PropType } from "vue";

const STATE = [
	"light",
	"dark",
	"default",
	"primary",
	"secondary",
	"success",
	"error",
	"warning",
	"border-light"
] as const;

const TYPE = ["solid", "dotted", "dashed", "double", "groove", "ridge", "inset", "outset"] as const;

export type DividerState = typeof STATE[number];
export type DividerType = typeof TYPE[number];

/**
 * A divider is a horizontal line that separates sections from one another.
 */
export default defineComponent({
	name: "Divider",
	props: {
		/**
		 * 'light' | 'dark' | 'default' | 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'border-light'
		 */
		state: {
			type: String as PropType<DividerState>,
			default: "default"
			// validator(value: DividerState): boolean {
			// 	return STATE.includes(value);
			// }
		},

		/**
		 * Size [1 - 10]
		 *
		 */
		size: {
			type: Number as PropType<1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10>,
			default: 1
			// validator(value: number): boolean {
			// 	return value >= 1 && value <= 10;
			// }
		},

		/**
		 * 	'solid' | 'dotted' | 'dashed' | 'double' | 'groove' | 'ridge' | 'inset' | 'outset'
		 */
		type: {
			type: String as PropType<DividerType>,
			default: "solid"
			// validator(value: DividerType): boolean {
			// 	return TYPE.includes(value);
			// }
		},

		/**
		 * 'horizontal' | 'vertical'
		 */
		direction: {
			type: String as PropType<"horizontal" | "vertical">,
			default: "horizontal"
			// validator(value: "horizontal" | "vertical"): boolean {
			// 	return ["horizontal", "vertical"].includes(value);
			// }
		},

		resizeSiblings: {
			type: Boolean,
			default: false
		},

		minResize: {
			type: Number,
			default: 200
		}
	},
	data() {
		return {
			resize: false,
			prevElementToUpdate: null as null | HTMLElement,
			nextElementToUpdate: null as null | HTMLElement,
			startX: 0,
			prevStartWidth: 0,
			nextStartWidth: 0,
			startY: 0,
			prevStartHeight: 0,
			nextStartHeight: 0
		};
	},
	mounted() {
		this.init();
	},

	methods: {
		init() {
			if (this.resizeSiblings) {
				const divider = this.$refs.divider as HTMLHRElement;

				this.prevElementToUpdate = divider.previousElementSibling as HTMLElement;
				this.nextElementToUpdate = divider.nextElementSibling as HTMLElement;

				divider.parentElement?.addEventListener("mousemove", this.resizeElement);
				divider.parentElement?.addEventListener("mouseleave", this.disableResize);
				divider.parentElement?.addEventListener("mouseup", this.disableResize);
			}
		},

		resizeElement(event: MouseEvent) {
			if (!this.prevElementToUpdate || !this.nextElementToUpdate) {
				return;
			}

			if (this.resize && this.direction === "vertical") {
				const prevWidth = this.prevStartWidth + (event.pageX - this.startX);
				const nextWidth = this.nextStartWidth - (event.pageX - this.startX);

				if (prevWidth >= this.minResize && nextWidth >= this.minResize) {
					this.prevElementToUpdate.style.width = `${prevWidth}px`;
					this.prevElementToUpdate.style.minWidth = `${prevWidth}px`;

					this.nextElementToUpdate.style.width = `${nextWidth}px`;
					this.nextElementToUpdate.style.minWidth = `${nextWidth}px`;
				}
			}

			if (this.resize && this.direction === "horizontal") {
				const preHeight = this.prevStartHeight + (event.pageY - this.startY);
				const nextHeight = this.nextStartHeight - (event.pageY - this.startY);
				if (preHeight >= this.minResize && nextHeight >= this.minResize) {
					this.prevElementToUpdate.style.height = `${preHeight}px`;
					this.prevElementToUpdate.style.minHeight = `${preHeight}px`;

					this.nextElementToUpdate.style.height = `${nextHeight}px`;
					this.nextElementToUpdate.style.minHeight = `${nextHeight}px`;
				}
			}
		},
		enableResize() {
			this.resize = true;
			this.$emit("resize-start", true);
		},
		disableResize() {
			this.resize = false;
			this.$emit("resize-stop", true);
		},
		handleMouseDown(event: MouseEvent) {
			this.enableResize();
			const divider = this.$refs.divider as HTMLHRElement;

			this.prevElementToUpdate = divider.previousElementSibling as HTMLElement;
			this.nextElementToUpdate = divider.nextElementSibling as HTMLElement;

			if (this.prevElementToUpdate && this.nextElementToUpdate && this.direction === "vertical") {
				this.startX = event.pageX;
				this.prevStartWidth = this.prevElementToUpdate.offsetWidth;
				this.nextStartWidth = this.nextElementToUpdate.offsetWidth;
			}
			if (this.prevElementToUpdate && this.nextElementToUpdate && this.direction === "horizontal") {
				this.startY = event.pageY;
				this.prevStartHeight = this.prevElementToUpdate.offsetHeight;
				this.nextStartHeight = this.nextElementToUpdate.offsetHeight;
			}
		}
	}
});
</script>
<style lang="scss">
@mixin flow-divider($colors) {
	hr.flow-divider {
		@each $type, $color in $colors {
			&[state="#{$type}"] {
				border-color: $color;
			}
		}
	}
}

$sizes: 1, 2, 3, 4, 5, 6, 7, 8, 9, 10;
$types: solid, dotted, dashed, double, groove, ridge, inset, outset;

hr.flow-divider[direction="horizontal"] {
	border-right: none;
	border-left: none;
	border-bottom: none;
	// width: 100%;
	flex: 1;

	@each $size in $sizes {
		&[size="#{$size}"] {
			border-top-width: $size + px;
		}
	}

	@each $type in $types {
		&[type="#{$type}"] {
			border-top-style: $type;
		}
	}
}
hr.flow-divider[direction="vertical"] {
	border-right: none;
	border-top: none;
	border-bottom: none;
	min-height: 12px;
	height: 100%;

	@each $size in $sizes {
		&[size="#{$size}"] {
			// width: $size + px;
			border-left-width: $size + px;
		}
	}

	@each $type in $types {
		&[type="#{$type}"] {
			border-left-style: $type;
		}
	}
}

hr.flow-divider[data-resize-siblings] {
	position: relative;
	&[direction="vertical"] {
		&:hover,
		&[data-active] {
			border-color: var(--primary-300);
			cursor: col-resize;
		}
		&:before {
			content: "";
			position: absolute;
			width: 500%;
			height: 500%;
			left: 50%;
			top: 50%;
			transform: translate(-50%, -50%);
			z-index: -1;
		}
	}
	&[direction="horizontal"] {
		&:hover,
		&[data-active] {
			border-color: var(--primary-300);
			cursor: row-resize;
		}
		&:before {
			content: "";
			position: absolute;
			width: 500%;
			height: 500%;
			left: 50%;
			top: 50%;
			transform: translate(-50%, -50%);
			z-index: -1;
		}
	}
}

$slideout-divider-colors: (
	"light": var(--slideout-font-light),
	"dark": var(--slideout-font-dark),
	"default": var(--slideout-border-light),
	"border-light": var(--slideout-border-light),
	"primary": var(--slideout-primary),
	"secondary": var(--slideout-secondary),
	"success": var(--slideout-success),
	"error": var(--slideout-danger),
	"warning": var(--slideout-warning)
);

//pass mode related variables
div.sideout-container {
	@include flow-divider($slideout-divider-colors);
}

$dashboard-divider-colors: (
	"light": var(--dashboard-font-light),
	"dark": var(--dashboard-font-dark),
	"default": var(--dashboard-border-light),
	"border-light": var(--dashboard-border-light),
	"primary": var(--dashboard-primary),
	"secondary": var(--dashboard-secondary),
	"success": var(--dashboard-success),
	"error": var(--dashboard-danger),
	"warning": var(--dashboard-warning)
);
@include flow-divider($dashboard-divider-colors);
</style>
